import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, ChevronRight, Search, CalendarDays } from "lucide-react";
import { listPackingLists } from "@/lib/api";
import { Input } from "@/components/ui/input";

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return `${WEEKDAY[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]} ${d.getFullYear()}`;
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const PackingLists = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [picker, setPicker] = useState(todayIso());

  useEffect(() => {
    listPackingLists()
      .then(setPlans)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return plans;
    return plans.filter((p) => {
      const pretty = fmtDate(p.date).toLowerCase();
      return p.date.includes(needle) || pretty.includes(needle);
    });
  }, [plans, q]);

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="packing-lists-page">
      <div className="label-xs mb-2">MODULE 08 / PACKING LISTS</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9]">
        Packing lists.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl">
        Internal-only checklist for the workshop run. Type a customer number
        and the row auto-fills with name, area and per-screen-type counts from
        their latest quote.
      </p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link
          to={`/packing-lists/${todayIso()}`}
          className="group bg-[#09090b] text-white p-6 hover:bg-[#27272a] transition-colors"
          data-testid="packing-list-today-cta"
        >
          <div className="label-xs !text-zinc-400">Today's packing</div>
          <div className="font-display text-2xl sm:text-3xl font-black tracking-tighter mt-1">
            {fmtDate(todayIso())}
          </div>
          <div className="font-mono text-xs text-zinc-400 mt-3 inline-flex items-center gap-1">
            Open editor <ChevronRight size={12} />
          </div>
        </Link>

        <div className="border border-zinc-300 p-6">
          <div className="label-xs">Jump to date</div>
          <div className="flex gap-2 mt-2">
            <input
              type="date"
              value={picker}
              onChange={(e) => setPicker(e.target.value)}
              className="border border-zinc-300 px-3 h-11 font-mono text-sm tabular-nums focus:outline-none focus:border-[#09090b]"
              data-testid="packing-list-date-picker"
            />
            <Link
              to={`/packing-lists/${picker}`}
              className="inline-flex items-center gap-1.5 bg-[#005bea] hover:bg-[#0048b8] text-white px-4 h-11 text-xs uppercase tracking-[0.18em] font-semibold transition-colors"
              data-testid="packing-list-jump-btn"
            >
              <CalendarDays size={13} /> Open
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="label-xs">Past lists</div>
          <div className="relative w-full max-w-xs">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 2026-04-29 or April"
              className="pl-9 h-10 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-[#09090b] font-mono"
              data-testid="packing-list-search"
            />
          </div>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-400 uppercase tracking-[0.18em] py-10 text-center">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="border border-dashed border-zinc-300 p-10 text-center"
            data-testid="packing-lists-empty"
          >
            <Package size={20} className="mx-auto text-zinc-300 mb-3" />
            <div className="font-display text-xl font-black tracking-tighter">
              {plans.length === 0 ? "No lists yet" : "No matches"}
            </div>
            {plans.length === 0 ? (
              <p className="text-zinc-500 text-sm mt-2">
                Tap "Today's packing" above to start your first list.
              </p>
            ) : null}
          </div>
        ) : (
          <ul className="border border-zinc-200 divide-y divide-zinc-100">
            {filtered.map((p) => (
              <li key={p.date}>
                <Link
                  to={`/packing-lists/${p.date}`}
                  className="flex items-center justify-between gap-4 p-5 hover:bg-zinc-50 transition-colors"
                  data-testid={`packing-list-row-${p.date}`}
                >
                  <div className="min-w-0">
                    <div className="font-display text-2xl font-black tracking-tighter">
                      {fmtDate(p.date)}
                    </div>
                    <div className="font-mono text-[11px] text-zinc-500 mt-1">
                      {p.items_count} customer{p.items_count === 1 ? "" : "s"}
                      {p.has_notes ? " · has notes" : ""}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-zinc-400 flex-none" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PackingLists;
